import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import './HostConference.css';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';

// Request camera + mic permissions on native platforms before getUserMedia
const requestMediaPermissions = async () => {
  if (!Capacitor.isNativePlatform()) return true;

  try {
    const { camera, microphone } = await Camera.requestPermissions({ permissions: ['camera', 'microphone'] });
    console.log('Permission status — camera:', camera, 'microphone:', microphone);
    if (camera === 'denied' || microphone === 'denied') {
      alert('Camera and microphone access are required for video conferencing. Please enable them in your device settings.');
      return false;
    }
    return true;
  } catch (err) {
    console.warn('Permission request error:', err);
    return true;
  }
};

const isNative = Capacitor.isNativePlatform();

let lastTranscriptionTime = Date.now();

const HostConference = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [room, setRoom] = useState('');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState([]);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [hostName, setHostName] = useState(''); // Host's name state
  const [clients, setClients] = useState([]);
  const [waitingGuests, setWaitingGuests] = useState([]); // Change from array of IDs to array of objects
  const [selectedClient, setSelectedClient] = useState('');
  const [sessionNotes, setSessionNotes] = useState([]); // Store session notes
  const [newSessionNote, setNewSessionNote] = useState(''); // For adding notes
  const [isRecording, setIsRecording] = useState(false); // Manage recording state
  const [transcription, setTranscription] = useState(''); // Store transcription text
  const [errorMessage, setErrorMessage] = useState('');
  const screenStreamRef = useRef(null);
  const peerConnections = useRef({});
  const socketRef = useRef(null);
  const transcriptionWatchdogInterval = 10000; // check every 10 seconds (adjust as needed)
  const transcriptionTimeoutThreshold = 30000; // if no text for 30 seconds, trigger a restart
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const navigate = useNavigate();

  // Server-side transcription states
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const recordingIntervalRef = useRef(null);
  const recordingStateRef = useRef(false);

  // Browser-based transcription states
  const [recognitionInstance, setRecognitionInstance] = useState(null); // Speech recognition instance

  // Diarization (speaker identification) toggle
  const [diarizeEnabled, setDiarizeEnabled] = useState(false);
  const micStream = useRef(null);

  const [areGuestsMuted, setAreGuestsMuted] = useState(false);
  const [isCallStarted, setIsCallStarted] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  

  let watchdogTimer = null;

useEffect(() => {
  if (socketRef.current) {
    const handleGuestWaiting = ({ guestId, guestName }) => {
      console.log(`Guest waiting: ${guestId} (${guestName})`);
      setWaitingGuests((prev) => {
        // Check if guest is already in the list
        if (!prev.find(guest => guest.guestId === guestId)) {
          return [...prev, { guestId, guestName: guestName || 'Unknown Guest' }];
        }
        return prev;
      });
    };

    socketRef.current.on('guest-waiting', handleGuestWaiting);

    return () => {
      socketRef.current.off('guest-waiting', handleGuestWaiting);
    };
  }
}, [socketRef.current]);
  
const admitGuest = (guestId) => {
  // Check if call has started first
  if (!isCallStarted) {
    alert('Please start the call first before admitting guests.');
    return;
  }

  console.log(`Admitting guest: ${guestId}`);
  socketRef.current.emit('admit-guest', { room, guestId });

  // Find the guest name from waiting list
  const guestInfo = waitingGuests.find(guest => guest.guestId === guestId);
  const guestName = guestInfo?.guestName || 'Guest';

  // Check if peer connection already exists
  let pc = peerConnections.current[guestId];
  if (!pc) {
    // Create new peer connection only if it doesn't exist
    pc = createPeerConnection(guestId, guestName);
    if (localStream) {
      addLocalTracks(pc, guestId);
    }
  }
  
  // Always send a fresh offer when admitting
  sendOffer(pc, guestId);
  
  // Remove guest from waiting list
  setWaitingGuests((prev) => prev.filter((guest) => guest.guestId !== guestId));
};
  
  // Authentication check
  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        navigate('/login');
        return;
      }
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Invalid token:', error);
      navigate('/login');
    }
  }, [navigate]);
  

    // Fetch host name and client list
    const fetchHostAndClients = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
  
      const headers = { Authorization: `Bearer ${token}` };
  
      try {
        // Fetch host name
        const hostResponse = await axios.get(`${apiUrl}/api/users/me`, { headers });
        setHostName(`${hostResponse.data.firstname} ${hostResponse.data.lastname}`);
  
        // Fetch client list
        const clientsResponse = await axios.get(`${apiUrl}/api/clients`, { headers });
        setClients(clientsResponse.data);
      } catch (error) {
        console.error('Error fetching host or clients:', error);
      }
    };

// In your useEffect that initializes the conference, replace this section:

useEffect(() => {
  const initializeConference = async () => {
    checkAuthentication();

    if (isAuthenticated) {
      const queryParams = new URLSearchParams(window.location.search);
      const roomParam = queryParams.get('room');
      setRoom(roomParam);
      console.log('Room:', roomParam);

      // Fetch host and client details
      await fetchHostAndClients();

      // Set up the WebSocket connection
      socketRef.current = io.connect('https://api.airflyp.com');
      socketRef.current.emit('join-room', String(roomParam));
      
      // FIX: Use roomParam instead of room here!
      socketRef.current.emit('host-joined', roomParam);

      console.log(`Host joined room: ${roomParam}`); // Add this log

      // Listen for socket events
socketRef.current.on('user-joined', ({ userId, userName }) => {
  console.log(`HOST: Guest joined: ${userId} (${userName})`);
  
  const pc = createPeerConnection(userId, userName);
  
  // Use the updated addLocalTracks function which handles the stream internally
  addLocalTracks(pc, userId);
  
  sendOffer(pc, userId);
});

      socketRef.current.on('answer', ({ answer, from }) => {
        console.log('Answer received from guest:', from);
        const pc = peerConnections.current[from];
        if (pc) {
          pc.setRemoteDescription(new RTCSessionDescription(answer)).catch((error) => {
            console.error('Error setting remote description from answer:', error);
          });
        }
      });

      socketRef.current.on('ice-candidate', ({ candidate, from }) => {
        console.log('ICE Candidate received from guest:', candidate);
        const pc = peerConnections.current[from];
        if (pc) {
          pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((error) => {
            console.error('Error adding ICE candidate:', error);
          });
        }
      });
    }
  };

  initializeConference();

  return () => {
    endCall();
  };
}, [checkAuthentication, isAuthenticated]); // Remove localStream from dependencies

// Browser-based transcription visibility change handler
useEffect(() => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden' && isRecording) {
      console.warn('Page is hidden. Attempting to maintain recording.');
      if (recognitionInstance) {
        recognitionInstance?.stop();
      } else if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
    } else if (document.visibilityState === 'visible' && isRecording) {
      console.info('Page is visible again. Restarting recognition.');
      if (recognitionInstance) {
        handleStartBrowserRecording();
      } else {
        handleStartServerRecording();
      }
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [isRecording, recognitionInstance, mediaRecorder]);

    const startCall = async () => {
      try {
        const hasPermission = await requestMediaPermissions();
        if (!hasPermission) return;
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setLocalStream(stream);

        // Disable video and audio tracks by default
      stream.getVideoTracks().forEach((track) => (track.enabled = videoEnabled));
      stream.getAudioTracks().forEach((track) => (track.enabled = audioEnabled));
    
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
    
        Object.keys(peerConnections.current).forEach((userId) => {
          addLocalTracks(peerConnections.current[userId], userId);
        });
    
        // Notify all guests that the host has started the call
        socketRef.current.emit('host-started-call', room);
    
        // Update call state
        setIsCallStarted(true); // Set call state to started
      } catch (error) {
        console.error('Error starting call:', error);
      }
    };

const addLocalTracks = (pc, userId) => {
  // Get stream from video element to avoid closure issues
  const localVideo = localVideoRef.current;
  const streamToUse = localVideo?.srcObject;
  
  if (!streamToUse) {
    console.warn(`HOST: No local stream available to add tracks for ${userId}`);
    return;
  }

  console.log(`HOST: Adding tracks to peer connection for user: ${userId}`);
  streamToUse.getTracks().forEach((track) => {
    const existingSender = pc.getSenders().find((sender) => sender.track === track);
    if (!existingSender) {
      console.log(`HOST: Adding ${track.kind} track to peer connection for user: ${userId}`);
      pc.addTrack(track, streamToUse);
    } else {
      console.log(`HOST: ${track.kind} track already exists for user: ${userId}, skipping`);
    }
  });
};
  

const createPeerConnection = (userId, userName = null) => {
  console.log('Creating peer connection for guest:', userId, 'with name:', userName);
  
  if (peerConnections.current[userId]) {
    console.warn(`PeerConnection already exists for user: ${userId}`);
    return peerConnections.current[userId];
  }

  const pc = new RTCPeerConnection({
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  });

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      console.log('Sending ICE candidate:', event.candidate);
      socketRef.current.emit('ice-candidate', {
        candidate: event.candidate,
        to: userId,
      });
    }
  };

  pc.ontrack = (event) => {
    console.log(`HOST: Received track event from ${userId}:`, event.streams);
    
    if (!event.streams || event.streams.length === 0) {
      console.warn(`HOST: No valid stream received from user: ${userId}`);
      return;
    }

const [remoteStream] = event.streams;
console.log(`HOST: Remote stream tracks:`, remoteStream.getTracks().map(t => `${t.kind}: ${t.enabled}`)); // ADD THIS LINE
console.log(`HOST: Processing stream from ${userId}, tracks:`, remoteStream.getTracks().length);

    setRemoteStreams((prevStreams) => {
      const existingStream = prevStreams.find((s) => s.userId === userId);
      if (existingStream) {
        console.warn(`HOST: Duplicate stream detected for user ${userId}. Updating existing stream.`);
        // Update the existing stream instead of skipping
        return prevStreams.map(stream => 
          stream.userId === userId 
            ? { ...stream, stream: remoteStream, name: userName || stream.name }
            : stream
        );
      }
      
      // Add new stream
      const guestName = userName || `Guest (${userId.slice(0, 6)})`;
      console.log(`HOST: Adding new remote stream for ${userId} with name: ${guestName}`);
      
      return [...prevStreams, { 
        userId, 
        stream: remoteStream, 
        name: guestName
      }];
    });

    // Set video element source
    setTimeout(() => {
      if (remoteVideoRefs.current[userId]) {
        remoteVideoRefs.current[userId].srcObject = remoteStream;
        console.log(`HOST: Set video element for ${userId}`);
      } else {
        console.warn(`HOST: No video element found for user: ${userId}`);
      }
    }, 100);
  };

  pc.onconnectionstatechange = () => {
    console.log(`HOST: Peer connection state for ${userId}:`, pc.connectionState);
  };

  pc.oniceconnectionstatechange = () => {
    console.log(`HOST: ICE connection state for ${userId}:`, pc.iceConnectionState);
  };

  peerConnections.current[userId] = pc;
  return pc;
};



  const sendOffer = async (pc, userId) => {
    try {
      console.log('Sending offer to user:', userId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current.emit('offer', { offer, to: userId });
      console.log('Offer sent to user:', userId);
    } catch (error) {
      console.error('Error sending offer:', error);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled;
        setVideoEnabled(track.enabled);
      });
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
        setAudioEnabled(track.enabled);
      });
    }
  };

const endCall = () => {
  console.log('Ending call...');
  
  // Stop any ongoing recording first
  if (isRecording) {
    if (recognitionInstance) {
      handleStopBrowserRecording();
    } else {
      handleStopServerRecording();
    }
  }
  
  // Notify all participants FIRST before cleaning up
  if (socketRef.current) {
    socketRef.current.emit('end-call', room);
  }
  
  // Then clean up host resources
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    setLocalStream(null);
  }

  Object.values(peerConnections.current).forEach((pc) => pc.close());
  peerConnections.current = {};
  setRemoteStreams([]);
  setAreGuestsMuted(false); // Reset mute state

  if (socketRef.current) {
    socketRef.current.disconnect();
  }
  
  // Reset call state
  setIsCallStarted(false);
};

  const muteAllGuests = () => {
    if (socketRef.current) {
      socketRef.current.emit('mute-all', { room });
      console.log('Sent mute-all request to all guests.');
      setAreGuestsMuted(true); // Update state to reflect mute action
    }
  };

const shareScreen = async () => {
  if (isScreenSharing) {
    // Stop screen sharing
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    setIsScreenSharing(false);

    // Revert to local video stream
    if (localStream) {
      Object.keys(peerConnections.current).forEach((userId) => {
        const pc = peerConnections.current[userId];
        pc.getSenders().forEach((sender) => {
          if (sender.track.kind === 'video') {
            sender.replaceTrack(localStream.getVideoTracks()[0]);
          }
        });
      });
    }
    return;
  }

  try {
    // Start screen sharing
    const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    screenStreamRef.current = screenStream;
    setIsScreenSharing(true);

    // Replace video track in all peer connections with screen sharing track
    Object.keys(peerConnections.current).forEach((userId) => {
      const pc = peerConnections.current[userId];
      pc.getSenders().forEach((sender) => {
        if (sender.track.kind === 'video') {
          sender.replaceTrack(screenStream.getVideoTracks()[0]);
        }
      });
    });

    // Handle screen sharing end
    screenStream.getVideoTracks()[0].onended = () => {
      console.log('Screen sharing ended');
      setIsScreenSharing(false);

      // Revert to local video stream
      if (localStream) {
        Object.keys(peerConnections.current).forEach((userId) => {
          const pc = peerConnections.current[userId];
          pc.getSenders().forEach((sender) => {
            if (sender.track.kind === 'video') {
              sender.replaceTrack(localStream.getVideoTracks()[0]);
            }
          });
        });
      }
    };
  } catch (error) {
    console.error('Error sharing screen:', error);
  }
};

  // Add a New Session Note
const handleAddSessionNote = async () => {
  if (!newSessionNote.trim()) {
    alert('Please enter a session note.');
    return;
  }

  try {
    const token = localStorage.getItem('token'); // Ensure token is defined here
    const headers = { Authorization: `Bearer ${token}` };
    await axios.post(
      `${apiUrl}/api/clients/${selectedClient}/session-notes`,
      { text: newSessionNote },
      { headers }
    );
    setNewSessionNote(''); // Clear the input field
    alert('Session note saved successfully.');
  } catch (error) {
    console.error('Failed to add session note:', error);
    setErrorMessage('Failed to save session note.');
  }
};

const handleFullscreen = (userId) => {
  const videoElement = remoteVideoRefs.current[userId];
  if (videoElement && videoElement.requestFullscreen) {
    videoElement.requestFullscreen().catch((error) => {
      console.error('Error trying to enter full screen mode:', error);
    });
  }
};

// Define the handleClientSelection function here
const handleClientSelection = (event) => {
  setSelectedClient(event.target.value);
  console.log('Selected client:', event.target.value); // Debugging log
};


navigator.mediaDevices.addEventListener('devicechange', () => {
  console.log('Audio devices changed. Reacquiring microphone stream...');
  if (isRecording) {
    if (micStream.current) {
      micStream.current.getTracks().forEach((track) => track.stop());
      micStream.current = null;
    }
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        micStream.current = stream;
        console.log('Reacquired microphone stream after device change.');
      })
      .catch((error) => {
        console.error('Error reacquiring microphone:', error);
      });
  }
});

// ==================== BROWSER-BASED TRANSCRIPTION ====================
const handleStartBrowserRecording = () => {
  if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
    alert('Your browser does not support speech recognition. Please use a supported browser like Google Chrome.');
    console.error('Browser does not support Speech Recognition API');
    return;
  }

  if (isRecording) {
    console.warn('Recording is already in progress.');
    return;
  }

  // Initialize microphone stream
  navigator.mediaDevices.getUserMedia({ audio: true })
  .then((stream) => {
    micStream.current = stream;
    console.log('Microphone stream initialized for browser transcription.');
  })
  .catch((error) => {
    console.error('Microphone access error:', error);
  });

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'en-US';

  recognition.onresult = (event) => {
    let interimTranscription = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      if (event.results[i].isFinal) {
        setTranscription((prev) => prev + event.results[i][0].transcript + ' ');
        setNewSessionNote((prev) => prev + event.results[i][0].transcript + ' ');
      } else {
        interimTranscription += event.results[i][0].transcript;
      }
    }
  };

  recognition.onerror = (event) => {
    console.error('Speech recognition error:', event.error);
    setIsRecording(false);
    if (['network', 'no-speech', 'audio-capture'].includes(event.error)) {
      setTimeout(() => handleStartBrowserRecording(), 4000);
    }
  };

  recognition.onend = () => {
    if (isRecording) {
      setTimeout(() => handleStartBrowserRecording(), 4000);
    }
  };

  recognition.start();
  setRecognitionInstance(recognition);
  setIsRecording(true);
  console.log('Browser-based recording started');
};

const handleStopBrowserRecording = () => {
  // Stop speech recognition
  if (recognitionInstance) {
    recognitionInstance.stop(); // Stop the recognition instance
    recognitionInstance.onend = null; // Remove the onend handler to prevent restarting
    setRecognitionInstance(null);
  }

  // Stop the microphone stream
  if (micStream.current) {
    micStream.current.getTracks().forEach((track) => {
      track.stop(); // Stop each track in the microphone stream
    });
    micStream.current = null; // Clear the mic stream reference
  }

  // Update recording state
  setIsRecording(false);
  console.log('Browser recording stopped and resources cleared.');
};

// ==================== SERVER-SIDE TRANSCRIPTION ====================
const handleStartServerRecording = async () => {
  try {
    console.log('Starting server-side transcription recording...');
    
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true
      } 
    });

    // Check supported MIME types in order of preference
    const mimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/wav',
      'audio/mp4',
      '' // Let browser choose
    ];

    let selectedMimeType = '';
    for (const mimeType of mimeTypes) {
      if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        console.log(`Using MIME type: ${mimeType || 'browser default'}`);
        break;
      }
    }

    let currentRecorder = new MediaRecorder(stream, {
      mimeType: selectedMimeType,
      audioBitsPerSecond: 64000
    });

    // Store chunks temporarily
    let tempAudioChunks = [];

    currentRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        console.log('Audio chunk received:', event.data.size, 'bytes');
        tempAudioChunks.push(event.data);
      }
    };

    currentRecorder.onstop = () => {
      console.log('MediaRecorder stopped, processing final chunks');
      if (tempAudioChunks.length > 0) {
        processChunksAsComplete(tempAudioChunks, currentRecorder.mimeType);
        tempAudioChunks = [];
      }
    };

    currentRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
    };

    setMediaRecorder(currentRecorder);
    setIsRecording(true);
    recordingStateRef.current = true;
    
    // Start recording continuously (no time slicing)
    currentRecorder.start();

    // Set up interval to stop/restart recorder every 10 seconds
    recordingIntervalRef.current = {
      recordingInterval: setInterval(() => {
        if (currentRecorder && currentRecorder.state === 'recording') {
          console.log('Stopping recorder to process complete audio segment...');
          currentRecorder.stop();
          
          // Create new recorder for next segment
          setTimeout(() => {
            if (recordingStateRef.current && recordingIntervalRef.current) {
              console.log('Creating new recorder for next segment...');
              
              // Create new recorder
              currentRecorder = new MediaRecorder(stream, {
                mimeType: selectedMimeType,
                audioBitsPerSecond: 64000
              });

              tempAudioChunks = [];

              currentRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                  tempAudioChunks.push(event.data);
                }
              };

              currentRecorder.onstop = () => {
                if (tempAudioChunks.length > 0) {
                  processChunksAsComplete(tempAudioChunks, currentRecorder.mimeType);
                  tempAudioChunks = [];
                }
              };

              currentRecorder.onerror = (event) => {
                console.error('MediaRecorder error:', event.error);
              };

              setMediaRecorder(currentRecorder);
              currentRecorder.start();
            }
          }, 100);
        }
      }, 10000), // 10 second intervals
      
      autoSaveInterval: setInterval(() => {
        if (recordingStateRef.current && newSessionNote.trim().length > 0) {
          saveTranscriptionAsNote();
        }
      }, 600000) // Auto-save every 10 minutes
    };

    console.log('Server-side recording started with 10-second complete segments');
    
  } catch (error) {
    console.error('Error starting recording:', error);
    setIsRecording(false);
    recordingStateRef.current = false;
  }
};

const processChunksAsComplete = (chunks, mimeType) => {
  if (chunks.length === 0) return;

  console.log(`Processing ${chunks.length} chunks as complete audio segment`);
  
  const audioBlob = new Blob(chunks, { type: mimeType || 'audio/webm' });
  
  if (audioBlob.size > 1000) {
    console.log(`Processing complete audio segment: ${audioBlob.size} bytes`);
    sendAudioToServer(audioBlob);
  }
};

const sendAudioToServer = async (audioBlob) => {
  try {
    console.log('Sending audio to server for transcription...', audioBlob.size, 'bytes');
    
    const formData = new FormData();
    
    let filename = 'recording.webm';
    if (audioBlob.type.includes('ogg')) {
      filename = 'recording.ogg';
    } else if (audioBlob.type.includes('wav')) {
      filename = 'recording.wav';
    } else if (audioBlob.type.includes('mp4')) {
      filename = 'recording.m4a';
    }
    
    formData.append('audio', audioBlob, filename);

    // Send diarization params if enabled
    if (diarizeEnabled) {
      formData.append('diarize', 'true');
      formData.append('coachName', hostName || 'Coach');
      const selectedClientObj = clients.find(c => c._id === selectedClient);
      const clientName = selectedClientObj
        ? `${selectedClientObj.firstName} ${selectedClientObj.lastName}`.trim()
        : 'Client';
      formData.append('clientName', clientName);
    }

    const response = await axios.post(
      `${apiUrl}/api/transcribe`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 60000,
      }
    );

    if (response.data && response.data.text && response.data.text.trim()) {
      const transcribedText = response.data.text.trim();
      console.log('Real-time transcription chunk:', transcribedText);
      
      const formattedText = transcribedText.endsWith('.') ? 
        transcribedText + ' ' : 
        transcribedText + '. ';
      
      setNewSessionNote(prev => prev + formattedText);
      setTranscription(prev => prev + formattedText);
      
      lastTranscriptionTime = Date.now();
    }

  } catch (error) {
    console.error('Error sending audio to server:', error);
    setErrorMessage('Transcription service error');
  }
};

const handleStopServerRecording = () => {
  console.log('Stopping server-side recording...');
  
  recordingStateRef.current = false;
  
  if (recordingIntervalRef.current?.recordingInterval) {
    clearInterval(recordingIntervalRef.current.recordingInterval);
  }
  
  if (recordingIntervalRef.current?.autoSaveInterval) {
    clearInterval(recordingIntervalRef.current.autoSaveInterval);
  }
  
  recordingIntervalRef.current = null;

  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
    
    // CRITICAL: Stop all tracks from the audio stream
    if (mediaRecorder.stream) {
      mediaRecorder.stream.getTracks().forEach(track => {
        console.log('Stopping audio track:', track.kind);
        track.stop();
      });
    }
  }

  // Also stop micStream if it exists (from the old implementation)
  if (micStream.current) {
    micStream.current.getTracks().forEach(track => {
      console.log('Stopping mic stream track:', track.kind);
      track.stop();
    });
    micStream.current = null;
  }

  setIsRecording(false);
  setMediaRecorder(null);
  console.log('Server-side recording completely stopped - all audio tracks terminated');
};

const saveTranscriptionAsNote = async () => {
  const token = localStorage.getItem('token');
  const newNote = {
    text: newSessionNote,
    timestamp: new Date(),
  };

  try {
    const response = await axios.post(
      `${apiUrl}/api/clients/${selectedClient}/session-notes`,
      newNote,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    console.log('✅ Transcription auto-saved as session note.');
  } catch (error) {
    console.error('❌ Failed to auto-save transcription:', error);
  }
};

return (
  <div className="conference-container-host">
    <h1>AirFlyp 1:1 Host Conference</h1>
    <h2>Host: {hostName}</h2> {/* Display the host's name */}

<div className="waiting-guests-container">
  <h2>Waiting Guests</h2>
  {!isCallStarted && waitingGuests.length > 0 && (
    <p className="call-start-reminder">⚠️ Please start the call first to admit guests</p>
  )}
  <ul className="waiting-guests-list">
    {waitingGuests.map((guest) => (
      <li key={guest.guestId}>
        <span className="waiting-guest-name">
          {guest.guestName || 'Unknown Guest'}
        </span>
        <button 
          className={`admit-button ${!isCallStarted ? 'disabled' : ''}`}
          onClick={() => admitGuest(guest.guestId)}
          disabled={!isCallStarted}
          title={!isCallStarted ? 'Start the call first' : 'Admit guest to call'}
        >
          {!isCallStarted ? 'Call Not Started' : 'Admit'}
        </button>
      </li>
    ))}
  </ul>
  {waitingGuests.length === 0 && (
    <p>No guests waiting</p>
  )}
</div>
  
    {/* Client Dropdown and Transcription Section */}
    <div className="top-section">
      <div className="dropdown-container">
        <label htmlFor="client-dropdown"><h3>Associate Call with Client:</h3></label>
        <select
          id="client-dropdown"
          value={selectedClient}
          onChange={handleClientSelection}
          className="client-dropdown"
        >
          <option value="" disabled>
            Select a client
          </option>
          {clients.map((client) => (
            <option key={client._id} value={client._id}>
              {client.firstName} {client.lastName}
            </option>
          ))}
        </select>
      </div>

  
      {/* Transcription Section */}
      {selectedClient && (
        <div className="transcription-section">
          <h3>Transcribe Your Video Call</h3>
          <textarea
            placeholder="Type or record your session note here..."
            value={newSessionNote}
            onChange={(e) => setNewSessionNote(e.target.value)}
            rows="5"
            className="transcription-textarea"
          />
         <button onClick={handleAddSessionNote} className="button main-button">
    Save Note
  </button>
  
          {/* Recording Controls */}
<div className="recording-controls">
  <div className="transcription-options">
    <h4>Choose Transcription Method:</h4>
    
    {!isRecording ? (
      <div className="recording-buttons">
        <button 
          onClick={handleStartBrowserRecording}
          className="start-recording-btn browser-transcription"
          title="Uses your browser's built-in speech recognition (faster, works offline)"
        >
          🎙️ Browser Transcription
        </button>
        
        <button 
          onClick={handleStartServerRecording}
          className="start-recording-btn server-transcription"
          title="Sends audio to server for transcription (more accurate, requires internet)"
        >
          🌐 Server Transcription
        </button>
      </div>
    ) : (
      <div className="recording-active">
        <button 
          onClick={recognitionInstance ? handleStopBrowserRecording : handleStopServerRecording}
          className="stop-recording-btn"
        >
          ⏹️ Stop Recording
        </button>
        <p className="recording-indicator">
          🔴 Recording... ({recognitionInstance ? 'Browser' : 'Server'} Mode)
        </p>
      </div>
    )}
    
    <div className="transcription-info">
      <small>
        <strong>Browser:</strong> Fast, private, works offline | 
        <strong> Server:</strong> More accurate, requires internet
      </small>

      {/* Diarization Toggle */}
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={diarizeEnabled}
          onChange={(e) => setDiarizeEnabled(e.target.checked)}
          disabled={isRecording}
          style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
        />
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          🗣️ Speaker Identification {diarizeEnabled ? '(On)' : '(Off)'}
        </span>
        {diarizeEnabled && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
            — {hostName || 'Coach'} & {(() => {
              const c = clients.find(cl => cl._id === selectedClient);
              return c ? `${c.firstName} ${c.lastName}` : 'Client';
            })()}
          </span>
        )}
      </label>
    </div>
  </div>
</div>
        </div>
      )}
    </div>
  
<div className="video-container-host">
  <div className={`video-grid ${
    remoteStreams.length === 0 ? 'host-only' : 
    remoteStreams.length === 1 ? 'two-videos' : 'multiple-videos'
  }`}>
    {/* Host Video */}
    <div className="host-video-container">
      <div className="host-video-label">HOST</div>
      <video
        ref={localVideoRef}
        autoPlay
        playsInline
        muted
        className="local-video-host"
      ></video>
    </div>

    {/* Guest Videos */}
    {remoteStreams.map(({ userId, stream, name, isMuted }) => (
      <div
        key={userId}
        className="remote-video-wrapper-host"
        onClick={() => handleFullscreen(userId)}
      >
        <div className="video-container">
          <video
            ref={(el) => {
              remoteVideoRefs.current[userId] = el;
              if (el && !el.srcObject) {
                el.srcObject = stream;
              }
            }}
            autoPlay
            playsInline
            className="remote-video-host"
          ></video>
          {isMuted && (
            <div className="mute-overlay">
              Muted
            </div>
          )}
        </div>
        <p className="guest-name">
          {name ? name : `Guest (${userId.slice(0, 6)})`}
        </p>
      </div>
    ))}
  </div>
</div>
  
{/* Control Buttons */}
<div className="button-container-host">
<button
  onClick={!isCallStarted ? startCall : undefined}
  disabled={isCallStarted}
>
  {isCallStarted ? 'Call Started' : 'Start Call'}
</button>
  <button onClick={toggleVideo}>
    {videoEnabled ? 'Turn Video Off' : 'Turn Video On'}
  </button>
  <button onClick={toggleAudio}>
    {audioEnabled ? 'Mute Audio' : 'Unmute Audio'}
  </button>
  {!isNative && (
    <button onClick={shareScreen}>
      {isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
    </button>
  )}
  <button
    onClick={() => {
      if (areGuestsMuted) {
        console.log('Guests are already muted.');
      } else {
        muteAllGuests();
      }
    }}
  >
    {areGuestsMuted ? 'Guests Are Muted' : 'Mute All Guests'}
  </button>
  <button onClick={endCall}>End Call</button>
</div>
  
  {/* Done and Back to Dashboard Buttons */}
  <div className="navigation-buttons">
    <button onClick={() => navigate('/clients')}>Done</button>
    <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
  </div>
  </div>
    );
  };
  
  export default HostConference;